import {useQuery} from "@apollo/client";
import {useContext, useState} from "react";
import {FiSettings} from "react-icons/fi";

import {ActionBarWrapper} from "@/components/actionBarWrapper/ActionBarWrapper.tsx";
import Button from "@/components/button/Button.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {TabContentContainer} from "@/components/layout/TabContentContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {useTable} from "@/lib/hooks/useTable.ts";
import {useTabs} from "@/lib/hooks/useTabs.tsx";
import {
  GQL_SUBSCRIBERS,
  GQL_SUBSCRIBERS_COUNTS,
  SubscriberCountResults,
  SubscribersPageResult,
} from "@/lib/queries/statuspages.ts";
import {SubscriptionType} from "@/lib/types/statuspages.ts";
import {StatusPageContext} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.utils.tsx";
import {StatusPageSubscribersActionBar} from "@/pages/statuspages/tabs/subscribers/actions/StatusPageSubscribersActionBar.tsx";
import {StatusPageSubscribersAllowBlackListActionButton} from "@/pages/statuspages/tabs/subscribers/actions/StatusPageSubscribersAllowBlackListActionButton.tsx";
import {DisabledNotificationsWarningPanel} from "@/pages/statuspages/tabs/subscribers/components/DisabledNotificationsWarningPanel.tsx";
import {SubscriptionSettingsModal} from "@/pages/statuspages/tabs/subscribers/components/SubscriptionSettingsModal.tsx";
import {StatusPageSubscribersTable} from "@/pages/statuspages/tabs/subscribers/StatusPageSubscribersTable.tsx";
import {subscriptionTypeNames} from "@/pages/statuspages/tabs/subscribers/StatusPageSubscribersTable.utils.tsx";
import {StatusPageSubscribersTableEmptyState} from "@/pages/statuspages/tabs/subscribers/StatusPageSubscribersTableEmptyState.tsx";

const SUBSCRIPTION_TABS = [
  SubscriptionType.EMAIL,
  SubscriptionType.SMS,
  SubscriptionType.SLACK,
  SubscriptionType.WEBHOOK,
];

export const StatusPageSubscribersTab = () => {
  const {statusPage} = useContext(StatusPageContext);
  // Another type starts on page 1 without the search, and Back restores a type with its own search and page.
  const {tab: activeTab, changeTab} = useTabs({
    defaultTab: SubscriptionType.EMAIL,
    urlTabParam: "subscription_type",
    tabList: SUBSCRIPTION_TABS,
    preserveSearchParams: ["tab"],
  });
  const {search, searchDebounced, searchChanged, discardPendingSearch, pagination, setPagination} = useTable();
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const {data: subscribersCountsData} = useQuery<SubscriberCountResults>(GQL_SUBSCRIBERS_COUNTS, {
    variables: {
      statusPageID: statusPage.id,
    },
  });
  const [sorting, setSorting] = useState<Sorting>({column: "target", direction: SortingDirection.Asc});

  const {data, previousData, loading} = useQuery<SubscribersPageResult>(GQL_SUBSCRIBERS, {
    variables: {
      statusPageID: statusPage.id,
      ...pagination,
      sort: sorting.direction == SortingDirection.Asc ? sorting.column : `-${sorting.column}`,
      query: searchDebounced,
      subscriptionType: activeTab,
    },
  });
  const currentData = data || previousData;
  const items = currentData?.subscribers.items || [];
  const total = currentData?.subscribers.total || 0;

  const sections = Object.fromEntries(
    SUBSCRIPTION_TABS.map((tab) => [
      tab,
      {id: tab, title: subscriptionTypeNames[tab], badgeText: subscribersCountsData?.[tab]?.total, isVisible: true},
    ])
  );

  return (
    <div className={"d-flex flex-column gap-4"}>
      <div className={"d-flex align-items-start justify-content-between"}>
        <div className={"d-flex flex-column gap-2"}>
          <span className={"pulseTextBodyBold"}>Subscribers</span>
          <span className={"pulseTextBodySmall pulse-color-gray-600"}>
            Manage current subscribers and invite new ones
          </span>
        </div>
        <div className={"d-flex gap-2"}>
          <StatusPageSubscribersAllowBlackListActionButton>
            Email Allow & Block List
          </StatusPageSubscribersAllowBlackListActionButton>
          <Button variant={"secondary"} onClick={() => setShowSettingsModal(true)}>
            <Icon icon={FiSettings} />
            Settings
          </Button>
        </div>
      </div>
      <TabContentContainer
        sections={sections}
        tab={activeTab}
        setTab={(tab) => {
          setRowSelection({});
          discardPendingSearch();
          changeTab(tab);
        }}
        variant="secondary"
        title={""}
        actionsBar={
          <ActionBarWrapper
            left={
              <SearchInput
                value={search}
                onChange={(v) => {
                  searchChanged(v);
                  setRowSelection({});
                }}
                variant="tableControlBar"
              />
            }
            right={<StatusPageSubscribersActionBar />}
          />
        }
      >
        <DisabledNotificationsWarningPanel
          statusPage={statusPage}
          selectedTab={activeTab}
          handleClick={() => setShowSettingsModal(true)}
        />
        <StatusPageSubscribersTable
          type={activeTab as Exclude<SubscriptionType, SubscriptionType.RSS>}
          items={items}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          sortColumn={sorting.column}
          sortDirection={sorting.direction}
          onSortingChange={(newSorting) => {
            setSorting(newSorting);
            setRowSelection({});
          }}
          noDataElement={
            !loading && (
              <StatusPageSubscribersTableEmptyState
                subscriptionType={activeTab as SubscriptionType}
                variant={searchDebounced.length > 0 ? "filter" : "general"}
              />
            )
          }
          pagination={pagination}
          totalRows={total}
          updatePagination={setPagination}
          paginationData={items}
        />
      </TabContentContainer>

      <SubscriptionSettingsModal
        show={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        statusPage={statusPage}
      />
    </div>
  );
};
